<template>
  <section class="dashboard-history" aria-label="Snapshot history">
    <h3>Snapshot history</h3>
    <p class="history-description">Compare saved days from the last 90 days.</p>
    <DashboardDisclosure label="Show historical values in a table" @toggle="onToggle">
      <p v-if="loading" role="status">Loading snapshot history…</p>
      <div v-else-if="error" role="alert">{{ error }} <button type="button" class="btn btn-sm btn-outline-secondary" @click="load(true)">Retry</button></div>
      <template v-else-if="history">
        <p v-if="history.unavailableDates.length" role="status">{{ history.unavailableDates.length }} saved snapshots could not be read. <button type="button" class="btn btn-sm btn-link" @click="load(true)">Retry</button></p>
        <p v-if="!history.rows.length">No saved history is available in this period yet.</p>
        <div v-else class="history-scroll" tabindex="0" role="region" aria-label="Historical dashboard values">
          <table class="table table-sm mb-0">
            <caption class="visually-hidden">Saved municipal dashboard snapshots, most recent first</caption>
            <thead><tr><th scope="col">Date</th><th scope="col">Active listings</th><th scope="col">Address verified</th><th scope="col">Compliance</th><th scope="col">Listed properties</th><th scope="col">Active licenses</th><th scope="col">Open violations</th></tr></thead>
            <tbody><tr v-for="row in displayRows" :key="row.date">
              <th scope="row"><time :datetime="row.generatedAt" :title="fmtDate(row.generatedAt, { withTime: true })">{{ row.date }}</time></th>
              <td>{{ displayCount(row.activeListings) }}</td><td>{{ displayPercent(row.verifiedShare) }}</td><td>{{ displayPercent(row.complianceRate) }}</td>
              <td>{{ displayCount(row.listedProperties) }}</td><td>{{ displayCount(row.activeLicenses) }}</td><td>{{ displayCount(row.openViolations) }}</td>
            </tr></tbody>
          </table>
        </div>
        <p class="history-footnote">One saved copy per date (Eastern time). Rebuilds replace that day’s copy. Gaps are days without a saved snapshot; — means a measure was unavailable. Compliance uses the options selected above: deferred {{ policyLabel(options.deferred) }}; licensed but not listed {{ policyLabel(options.unlisted) }}; pending licenses {{ policyLabel(options.pending) }}. Exclusions apply first; each property counts once. Older snapshots without the counts needed for the selected calculation show —. Open violations count properties and include deferrals.</p>
      </template>
    </DashboardDisclosure>
  </section>
</template>

<script setup>
/* global defineProps */
import { computed, inject, toRef, watch } from 'vue';
import { DEFAULT_COMPLIANCE_OPTIONS, normalizeComplianceOptions } from '@strenforcement/domain/dashboardCompliance';
import { dashboardHistoryKey, useDashboardHistory } from './useDashboardHistory';
import { historyComplianceRate } from './dashboardTrendData';
import DashboardDisclosure from './DashboardDisclosure.vue';
import { fmtDate, fmtInt, fmtPct } from '../../../../dashboard/public/js/format.js';
const props = defineProps({ cityId: { type: String, required: true }, complianceOptions: { type: Object, default: () => ({ ...DEFAULT_COMPLIANCE_OPTIONS }) } });
const sharedHistory = inject(dashboardHistoryKey, null) || useDashboardHistory(toRef(props, 'cityId'));
const { history, loading, error, load } = sharedHistory;
const options = computed(() => normalizeComplianceOptions(props.complianceOptions));
const policyLabel = value => value === 'noncompliant' ? 'non-compliant' : value;
const displayRows = computed(() => (history.value?.rows || []).map(row => ({ ...row, complianceRate: historyComplianceRate(row, options.value) })));
const displayCount = value => value == null ? '—' : fmtInt(value);
const displayPercent = value => value == null ? '—' : fmtPct(value);
let expanded = false;
function onToggle(open) {
  expanded = open;
  if (open && !history.value && !loading.value) load();
}
watch(() => props.cityId, () => {
  if (expanded) load();
});
</script>

<style scoped>
.dashboard-history { margin-top: 28px; padding: 20px; background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); box-shadow: var(--shadow-sm); }
h3 { font-size: 15px; font-weight: 600; margin: 0 0 6px; }
.history-description, .history-footnote, p { font-size: 12px; color: var(--text-secondary); }
.history-description { margin: 0; }
.history-footnote { margin: 12px 0 0; line-height: 1.6; }
.history-scroll { overflow: auto; max-height: 420px; }
table { font-size: 12px; white-space: nowrap; font-variant-numeric: tabular-nums; }
th, td { padding: 10px 12px; text-align: right; border-color: var(--border); }
th:first-child { text-align: left; }
thead th { position: sticky; top: 0; background: var(--surface); color: var(--text-secondary); font-weight: 500; }
tbody th { font-weight: 400; }
</style>
